import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../config/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await API.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
    } catch {
      // Server always returns 200 for security; any network error shown here
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'h-[52px] w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-[0.95rem] text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-brand focus:ring-4 focus:ring-brand-light disabled:cursor-not-allowed disabled:bg-slate-100';

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center bg-white p-6">
      <div className="w-full max-w-[460px] rounded-3xl bg-white p-10 shadow-[0_10px_30px_rgba(0,0,0,0.1)]">
        <img
          src="https://qure.science/images/qure.webp"
          alt="Qure"
          className="mb-8 h-auto w-[140px]"
          style={{ filter: 'invert(44%) sepia(91%) saturate(2044%) hue-rotate(347deg) brightness(99%) contrast(95%)' }}
          onError={(e) => { e.target.src = 'https://via.placeholder.com/140x60?text=Qure'; }}
        />

        {submitted ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <i className="bi bi-envelope-check text-3xl text-green-600" />
            </div>
            <h2 className="mb-2 text-xl font-semibold text-gray-800">Check your email</h2>
            <p className="mb-6 text-gray-500 text-sm">
              If <strong>{email}</strong> is registered, you'll receive a password reset link shortly.
            </p>
            <Link to="/login" className="text-brand hover:text-brand-dark text-sm font-medium no-underline">
              ← Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mb-2 text-[1.6rem] font-semibold text-qure-secondary">Forgot Password</h1>
            <p className="mb-7 text-gray-500 text-sm">
              Enter the email address on your account and we'll send you a reset link.
            </p>

            {error && (
              <div className="mb-5 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 text-red-700 text-sm">
                <i className="bi bi-exclamation-circle-fill shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <input
                  type="email"
                  className={inputClass}
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="full-width-btn flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-brand text-[0.95rem] font-medium text-white transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send" />
                    Send Reset Link
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-[0.9rem] text-gray-500 no-underline hover:text-brand">
                ← Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
